import {Component, Fragment} from 'react';
import {formatPhone} from '@deliveriesondemand/dod-utils';
import {TriBool} from '@deliveriesondemand/dod-data';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import handlers from 'react-handlers';
import MaskedInput from 'react-maskedinput';
import moment from 'moment';
import injectSheet from 'react-jss';

import Address from '-/components/general/Address';
import Step from '-/components/general/Step';
import PhoneInput from '-/components/fields/PhoneInput';
import EmailInput from '-/components/fields/EmailInput';
import OccupancyInput from '-/components/fields/OccupancyInput';
import TextInput from '-/components/fields/TextInput';
import NameInput from '-/components/fields/NameInput';
import BuyerInfoService from '-/services/BuyerInfoService';
import NavigationService from '-/services/NavigationService';
import UIService from '-/services/UIService';
import FunnelService from '-/services/FunnelService';
import {Control, ControlGroup} from '-/components/fields/Control';
import ApiService from '-/services/ApiService';
import PriorAddress from '-/components/fields/PriorAddress';
import CreditorService from '-/services/CreditorService';
import DealInfoService from '-/services/DealInfoService';
import SessionService from '-/services/SessionService';
import DateInput from '-/components/fields/DateInput';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import OptInMessage from '-/components/general/OptInMessage';
import {renderFieldErrors, isMobile, isNewOffer} from '-/util/util';
import {capitalize, get, isFinite} from '-/util/lodash';
import {RadioGroup, Radio} from '-/components/fields/RadioGroup';

const hasOffer = !isNewOffer();

class SSNInput extends Component {
    getValue() {
        const {value} = this.refs.input.input;
        if (value) {
            return value.match(/(\d+)/g).join('');
        }
    }

    handleChange() {
        const {onChange} = this.props;
        onChange(this.getValue());
    }

    handleFocus() {
        this.refs.input.input.select();
    }

    render() {
        return (
            <MaskedInput
                ref="input"
                type="tel"
                mask="111-11-1111"
                placeholder="XXX-XX-XXXX"
                placeholderChar=" "
                size="11"
                onFocus={this.handleFocus.bind(this)}
                {...this.props}
                onChange={this.handleChange.bind(this)} />
        );
    }
}

const styles = theme => ({
    copyLink: {
        textDecoration: 'underline',
        ...isMobile() && {
            display: 'block'
        },
        flex: 1,
        fontSize: 14,
        color: theme.colors.link,
        cursor: 'pointer'
    },
    legend: {
        width: '100%'
    },
    marginBottom: {
        marginBottom: 20
    }
});

class BuyerInfo extends Component {
    static propTypes = {
        onSaveAndExitClick: PropTypes.func,
        onReviewClick: PropTypes.func
    };

    static defaultProps = {
        onSaveAndExitClick: () => {},
        onReviewClick: () => {}
    };

    constructor(props) {
        super(props);

        const {cellphoneOptIn} = props;

        this.state = {
            errorMessages: [],
            hasExistingOptIn: cellphoneOptIn,
            showErrors: true,
            validationMode: 'basic'
        };

        this.onNextClick = this.onNextClick.bind(this);
        this.onSaveAndExitClick = this.onSaveAndExitClick.bind(this);
        this.onReviewClick = this.onReviewClick.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
        this.onYearsAtAddressChange = this.onYearsAtAddressChange.bind(this);
        this.onMonthsAtAddressChange = this.onMonthsAtAddressChange.bind(this);
    }

    componentDidMount() {
        const isCoApplicant = NavigationService.isCoApplicant();
        
    
        if (isCoApplicant) {
            const street1 = get(this.props, 'address.street1');
            const {coApplicantType} = NavigationService.getReducerState();

            // only auto-populate if type is 'spouse' AND if we've haven't already saved info for the co-applicant
            if (coApplicantType === 'spouse' && !street1) {
                BuyerInfoService.copyFromApplicantToCoApplicant();
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        const {isCoApplicant} = this.props;
        if (nextProps.isCoApplicant !== isCoApplicant) {
            this.setState({
                errorMessages: [],
                showErrors: false
            });
        }
    }

    getBaseErrorMessages() {
        const {name, occupancy, dob, email, hasDobValidation, ssn} = this.refs;
        const {isCoApplicant, isCCRMode} = this.props;
        const showDOBField = (isCoApplicant || !hasDobValidation);
        const emailValue = email.value;
        const messages = [
            ...name.getErrors('Name')
        ];

        if (emailValue.length || (!isCCRMode && !isCoApplicant)) {
            messages.push(...email.getErrors('Email'));
        }

        if (showDOBField) {
            if (dob) {
                const momentDate = moment(dob.value, 'MM/DD/YYYY', true);
                if (!momentDate.isValid()) {
                    messages.push('Please enter a date of birth with the following format: MM/DD/YYYY');
                } else if (momentDate.diff(moment()) > 0) {
                    messages.push('Date of birth specified is in the future. Please enter accurate date of birth.');
                }
            }
        }

        if (ssn) {
            const ssnValue = ssn.getValue();
            const validOrEmpty = !ssnValue || ssnValue && ssnValue.length === 9;

            if (!validOrEmpty) {
                messages.push('SSN must contain 9 digits');
            }
        }

        if (occupancy) {
            messages.push(...occupancy.getErrors());
        }

        return messages;
    }

    getErrorMessages() {
        const {cellphone, address, priorAddresses} = this.refs;
        const messages = this.getBaseErrorMessages();
        const yearsNeeded = CreditorService.getYearsResidencyHistoryRequired();
        const monthsNeeded = yearsNeeded * 12;

        messages.push(...address.getErrors('Address'));

        if (!cellphone.isValid()) {
            messages.push('Please enter a cell phone number');
        }

        if (this.getMonthsAtCurrentAddress() < monthsNeeded && priorAddresses) {
            messages.push(...priorAddresses.getErrors());
        }
        return messages;
    }

    onInputChange() {
        const {showErrors, validationMode} = this.state;

        if (showErrors) {
            setTimeout(() => {
                const errorMessages = validationMode === 'basic' ? this.getBaseErrorMessages() : this.getErrorMessages();

                this.setState({errorMessages});
            }, 0);
        }
    }

    onClickIfValid(fn) {
        const errorMessages = this.getErrorMessages();
        this.setState({
            errorMessages,
            showErrors: true,
            validationMode: 'full'
        });

        if (errorMessages.length <= 0) {
            fn();
        }
    }

    onSaveAndExitClick() {
        const {onSaveAndExitClick} = this.props;
        const errorMessages = this.getBaseErrorMessages();

        this.setState({
            errorMessages,
            showErrors: true,
            validationMode: 'basic'
        });

        if (!errorMessages.length) {
            onSaveAndExitClick();
        }
    }

    onNextClick() {
        this.onClickIfValid(() => {
            ApiService.save('buyerInfo').then(() => {
                const {isCoApplicant, isDMSActive} = this.props;
                const isCashDeal = DealInfoService.isCashDeal();
                const nextStep = isCoApplicant || isDMSActive ? 'employmentInfo' : 'vehicleInfo';

                UIService.stepCompleted('buyerInfo');
                NavigationService.next(isCashDeal ? 'finalReview' : nextStep);
            });
        });
    }

    onReviewClick() {
        const {onReviewClick} = this.props;
        this.onClickIfValid(onReviewClick);
    }

    getMonthsAtCurrentAddress() {
        const {monthsAtAddress, yearsAtAddress} = this.props;
        const years = parseInt(yearsAtAddress, 10) || 0;
        const months = parseInt(monthsAtAddress, 10) || 0;

        return (years * 12) + months;
    }

    renderPriorAddressIfNeeded() {
        const {priorAddresses, onPriorAddressChange} = this.props;

        const monthsAtAddress = this.getMonthsAtCurrentAddress();
        const yearsRequired = CreditorService.getYearsResidencyHistoryRequired();
        const monthsRequired = yearsRequired * 12;

        if (monthsAtAddress < monthsRequired) {
            return (
                <PriorAddress
                    ref="priorAddresses"
                    onChange={handlers(onPriorAddressChange, this.onInputChange)}
                    value={priorAddresses}
                    monthsNeeded={monthsRequired - monthsAtAddress} />
            );
        }

        return null;
    }

    onYearsAtAddressChange(value) {
        const {onYearsAtAddressChange} = this.props;
        onYearsAtAddressChange(value);
        setTimeout(() => this.onInputChange(), 0);
    }

    onMonthsAtAddressChange(value) {
        const {onMonthsAtAddressChange} = this.props;
        const setValue = value ? value : undefined;
        onMonthsAtAddressChange(setValue);
        setTimeout(() => this.onInputChange(), 0);
    }

    handleOptInCheckboxChange = ({checked}) => {
        const {onOptIn} = this.props;

        onOptIn(checked);
    };

    copyInfo = () => {
        BuyerInfoService.copyFromApplicantToCoApplicant();
    };

    clearInfo = () => {
        BuyerInfoService.clearCoApplicant(true);
    };

    render() {
        const {
            apiErrors = [],
            creditLocked,
            onCreditLockedChange,
            hasOffer,
            onPreviousClick,
            occupancy,
            monthlyPayment,
            additionalDetails,
            onTypeChange,
            onAdditionalDetailsChange,
            onMonthlyPaymentChange,
            name,
            email,
            onNameChange,
            onEmailChange,
            isCashDeal,
            isCoApplicant,
            isDealer,
            // phones
            onCellphoneChange,
            cellphone,
            // address
            onAddressChange,
            address,
            yearsAtAddress,
            monthsAtAddress,
            // ssn
            lastFour,
            needsSSN,
            ssnComplete,
            ssn,
            onSSNChange,
            dob,
            hasDobValidation,
            onDOBChange,
            account,
            onAccountChange,
            classes = {}
        } = this.props;

        const {errorMessages = [], hasExistingOptIn, showErrors} = this.state;
        const showDOBField = (isCoApplicant || !hasDobValidation);
        const showSSNField = (needsSSN || !lastFour) && !ssnComplete && !isCashDeal;

        const renderTitle = () => {
            if (isCoApplicant) {
                return (
                    <Fragment>
                        <div>Co-Applicant Information</div>
                        <div style={{display: 'flex'}}>
                            <a className={classes.copyLink} onClick={this.copyInfo}>
                                Copy from Applicant
                            </a>
                            <a className={classes.copyLink} onClick={this.clearInfo} style={{textAlign: 'right'}}>
                                Remove Co-Applicant
                            </a>
                        </div>
                        
                    </Fragment>
                );
            } else {
                return <span>Applicant Information</span>;
            }
        };
        const optInMessageProps = {
            template: CreditorService.getOptInMessage(),
            creditorName: CreditorService.getName(),
            name,
            cellphone: formatPhone(cellphone)
        };

        return (
            <Step onSaveAndExitClick={this.onSaveAndExitClick} onPreviousClick={onPreviousClick} onNextClick={this.onNextClick} onReviewClick={this.onReviewClick} showPreviousBtn={false}>
                <Form>
                    <ControlGroup labelWidth={200}>
                        <Legend className={classes.legend}>{renderTitle()}</Legend>
                        <p className="info">Please verify that all information is correct.</p>
                        <Control>
                            <NameInput
                                ref="name"
                                label="Name"
                                placeholder="First Name Last Name"
                                value={name}
                                onChange={handlers(onNameChange, this.onInputChange)} />
                        </Control>
                        {showDOBField && (
                            <Control>
                                <DateInput
                                    ref="dob"
                                    label="DOB"
                                    placeholder="MM/DD/YYYY"
                                    value={dob}
                                    onChange={handlers(onDOBChange, this.onInputChange)} />
                            </Control>
                        )}
                        {!hasOffer && !isDealer && (
                            <Control>
                                <TextInput
                                    ref="account"
                                    label="Account"
                                    placeholder="8675309"
                                    value={account}
                                    onChange={handlers(onAccountChange, this.onInputChange)} />
                            </Control>
                        )}
                        {showSSNField && (
                            <Control>
                                <SSNInput
                                    ref="ssn"
                                    label="SSN"
                                    value={ssn}
                                    onChange={handlers(onSSNChange, this.onInputChange)} />
                            </Control>
                        )}
                        <Address
                            ref="address"
                            value={address}
                            onChange={handlers(onAddressChange, this.onInputChange)} />
                        <Control>
                            <EmailInput
                                ref="email"
                                value={email}
                                label="Email"
                                placeholder="name@example.com"
                                onChange={handlers(onEmailChange, this.onInputChange)} />
                        </Control>
                        <Control>
                            <PhoneInput
                                ref="cellphone"
                                value={cellphone}
                                label="Cellphone"
                                placeholder="XXX-XXX-XXXX"
                                onChange={handlers(onCellphoneChange, this.onInputChange)} />
                        </Control>
                    </ControlGroup>
                    {!hasExistingOptIn && (
                        <OptInMessage {...optInMessageProps} onChange={this.handleOptInCheckboxChange} />
                    )}

                    {!isCashDeal && 
                        <Fragment>
                            <div className={classes.marginBottom}>
                                <Legend className={classes.legend}>Is your credit currently locked/frozen?</Legend>
                                <RadioGroup labelWidth={200} vertical={isMobile()}>
                                    {TriBool.map(value => {
                                        return (
                                            <Radio
                                                key={`${value}-box`}
                                                name="creditLocked"
                                                value={value}
                                                label={capitalize(value)}
                                                checked={creditLocked === value}
                                                onChange={onCreditLockedChange} />
                                        );
                                    })}
                                </RadioGroup>
                            </div>

                            <OccupancyInput
                                ref="occupancy"
                                type={occupancy}
                                monthlyPayment={monthlyPayment}
                                additionalDetails={additionalDetails}
                                yearsAtAddress={yearsAtAddress}
                                monthsAtAddress={monthsAtAddress}
                                dob={dob}
                                onTypeChange={handlers(onTypeChange, this.onInputChange)}
                                onAdditionalDetailsChange={handlers(onAdditionalDetailsChange, this.onInputChange)}
                                onMonthlyPaymentChange={handlers(onMonthlyPaymentChange, this.onInputChange)}
                                onYearsAtAddressChange={this.onYearsAtAddressChange}
                                onMonthsAtAddressChange={this.onMonthsAtAddressChange} />

                            {this.renderPriorAddressIfNeeded()}
                        </Fragment>
                    }
                    {showErrors ? renderFieldErrors([...errorMessages, ...apiErrors]) : null}
                </Form>
            </Step>
        );
    }
}

const mapStateToProps = state => {
    const cellphoneOptIn = get(state, 'buyerInfo.buyerInfo.cellphoneOptIn', false);
    const validationOptions = get(state, 'config.validationOptions', []);
    const hasDobValidation = validationOptions.includes('dob');
    const dealType = get(state, 'creditor.type', 'creditUnion');
    const isCashDeal = DealInfoService.isCashDeal();
    const apiErrors = ApiService.getErrors();
    const isDMSActive = get(state, 'creditor.isDMSActive', false);

    return {
        apiErrors,
        cellphoneOptIn,
        isCashDeal,
        isCCRMode: SessionService.isCCRMode(),
        isCU: dealType === 'creditUnion',
        isDealer: dealType === 'dealer',
        isDMSActive,
        hasOffer,
        hasDobValidation,
        ...BuyerInfoService.getBuyerInfoForCurrentApplicant(),
        originalBuyerAddress: BuyerInfoService.getReducerState().buyerInfo.address,
        isCoApplicant: NavigationService.isCoApplicant()
    };
};

const mapDispatchToProps = () => {
    return {
        onCreditLockedChange: e => {
            const {value} = e.target;

            // Distribution only, so we can tell whether this question earns its place on
            // the form. A yes/no, never free text.
            FunnelService.fieldAnswered('creditLocked', value);
            BuyerInfoService.set('creditLocked', value);
        },
        onPreviousClick: () => {
            NavigationService.next('validationInfo');
        },
        onSaveAndExitClick: () => {
            ApiService.explicitSave('buyerInfo');
        },
        onReviewClick: () => {
            NavigationService.next('finalReview');
        },
        onTypeChange: value => {
            BuyerInfoService.set('occupancy', value);
        },
        onAdditionalDetailsChange: value => {
            BuyerInfoService.set('additionalDetails', value);
        },
        onMonthlyPaymentChange: value => {
            BuyerInfoService.set('monthlyPayment', parseInt(value, 10));
        },
        onAddressChange: value => {
            BuyerInfoService.set('address', value);
        },
        onYearsAtAddressChange: value => {
            BuyerInfoService.set('yearsAtAddress', parseInt(value, 10));
        },
        onMonthsAtAddressChange: value => {
            BuyerInfoService.set('monthsAtAddress', parseInt(value, 10));
        },
        onPriorAddressChange: value => {
            if (Array.isArray(value)) {
                for(var i = 0; i < value.length; i++) {
                    const address = value[i];
                    const {monthsAtPriorAddress: monthsRaw, yearsAtPriorAddress: yearsRaw} = address;
                    const months = isFinite(monthsRaw) ? monthsRaw : 0;
                    const years = isFinite(yearsRaw) ? yearsRaw : 0;
                    // enforce numbers
                    value[i].monthsAtPriorAddress = months;
                    value[i].yearsAtPriorAddress = years;
                }
            }

            BuyerInfoService.set('priorAddresses', value);
        },
        onNameChange: value => {
            BuyerInfoService.set('name', value);
        },
        onSSNChange: value => {
            BuyerInfoService.set('ssn', value);
        },
        onDOBChange: value => {
            BuyerInfoService.set('dob', value);
        },
        onAccountChange: value => {
            BuyerInfoService.set('account', value);
        },
        onCellphoneChange: value => {
            BuyerInfoService.set('cellphone', value);
        },
        onEmailChange: value => {
            BuyerInfoService.set('email', value);
        },
        onOptIn: value => {
            BuyerInfoService.set('cellphoneOptIn', value);
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(BuyerInfo));
